import { ContributionInput } from 'core/graphql';

/**
 * ERP #83 — the agreement schedule is saved as an UPSERT by
 * `updateStudentWithAgreement`: a row with `id` is updated in place (its
 * Fakturownia invoices, manual payment and refund corrections stay attached),
 * a row without `id` is created, and an existing row whose id is missing from
 * the list is deleted. Every caller that sends the schedule must therefore
 * send the ids of the rows that came from the server — before this, each save
 * recreated every contribution and silently unlinked all invoices.
 *
 * Pure helpers (no React, no i18n instance) so both the edit popup and the
 * «+ Взнос в план» action build the payload the same way, and so they are
 * unit-testable.
 */

export interface IScheduleRow {
  /** Server contribution id, or a client-only placeholder (nanoid) for a new row. */
  id?: string | number | null;
  /** ISO date `YYYY-MM-DD`. */
  date: string;
  sum: number;
}

export interface IScheduleNode {
  id: string;
  date: string;
  amountForeign?: number | null;
  amountByn?: number | null;
}

/**
 * Maps form rows to `ContributionInput[]`, keeping `id` ONLY for rows whose id
 * belongs to the agreement's loaded schedule. A new row carries a client-side
 * nanoid placeholder, which must never reach the backend
 * (`contribution_not_in_agreement`).
 */
export const toContributionInputs = (
  rows: IScheduleRow[],
  existingIds: Iterable<string | number>,
): ContributionInput[] => {
  const known = new Set(Array.from(existingIds, (id) => String(id)));
  return rows.map(({ id, date, sum }) => {
    const key = id === null || id === undefined ? '' : String(id);
    return key && known.has(key) ? { id: key, date, sum } : { date, sum };
  });
};

/** Amount of a loaded contribution, mirroring `transformAgreementData`. */
const nodeSum = (node: IScheduleNode): number =>
  Number(node.amountForeign || node.amountByn || 0);

/**
 * The whole loaded schedule (every row with its id) plus one new row without
 * an id — the payload of «+ Взнос в план».
 */
export const appendContribution = (
  nodes: IScheduleNode[],
  row: { date: string; sum: number },
): ContributionInput[] => [
  ...nodes.map((node) => ({
    id: node.id,
    date: node.date,
    sum: nodeSum(node),
  })),
  { date: row.date, sum: row.sum },
];

type TranslateFn = (
  key: string,
  options?: { row?: string; date?: string },
) => unknown;

const SCHEDULE_ERROR_KEYS: Record<string, string> = {
  CONTRIBUTION_HAS_CORRECTIONS: 'common.contributionSchedule.hasCorrections',
  CONTRIBUTION_HAS_PAYMENT: 'common.contributionSchedule.hasPayment',
  CONTRIBUTION_HAS_INVOICE: 'common.contributionSchedule.hasInvoice',
  CONTRIBUTION_HAS_CALENDAR_OVERRIDE:
    'common.contributionSchedule.hasCalendarOverride',
  CONTRIBUTION_HAS_OPEN_ISSUE_ATTEMPT:
    'common.contributionSchedule.hasOpenIssueAttempt',
  contribution_not_in_agreement: 'common.contributionSchedule.notInAgreement',
  duplicate_contribution: 'common.contributionSchedule.duplicate',
};

export const isContributionScheduleErrorCode = (
  code?: string | null,
): boolean => Boolean(code && SCHEDULE_ERROR_KEYS[code]);

/**
 * Human message for a schedule-upsert error of `updateStudentWithAgreement`
 * (`customErrors` with field `contributions`), or `null` for any other code.
 * The backend message is English and carries the row's ISO date
 * («Contribution of 2026-03-10 …») — it is only mined for that date.
 */
export const contributionScheduleErrorMessage = (
  t: TranslateFn,
  error?: { code?: string | null; message?: string | null } | null,
): string | null => {
  const key = error?.code ? SCHEDULE_ERROR_KEYS[error.code] : undefined;
  if (!key) {
    return null;
  }
  const iso = error?.message?.match(/(\d{4})-(\d{2})-(\d{2})/);
  const row = iso
    ? `${t('common.contributionSchedule.rowOfDate', {
        date: `${iso[3]}.${iso[2]}.${iso[1]}`,
      })}`
    : `${t('common.contributionSchedule.row')}`;
  return `${t(key, { row })}`;
};
